export type DocumentType =
	| 'items'
	| 'reservations'
	| 'checkouts'
	| 'users'
	| 'kits'
	| 'tickets'
	| 'spotchecks'
	| 'locations'
	| 'booking_portal';

/** Single-column sort for resource search filters (API-owned; not tied to @tanstack/react-table). */
export interface ResourceSort {
	id: string;
	desc: boolean;
}

export type ResourceSortState = ResourceSort[];

export enum ResourceType {
	ITEMS = 'items',
	CUSTOMERS = 'customers',
	RESERVATIONS = 'reservations',
	CHECKOUTS = 'checkouts',
	TICKETS = 'tickets',
	KITS = 'kits',
	USERS = 'users',
	GROUPS = 'groups',
	SPOTCHECKS = 'spotchecks',
	CATEGORIES = 'categories',
	BOOKING_PORTAL = 'booking_portal',
	DOCUMENTS = 'documents',
	ATTACHMENTS = 'attachments',
	LOCATIONS = 'locations',
	INTEGRATIONS = 'integrations',
	SYNCS = 'syncs',
	TEMPLATES = 'templates',
	WEBHOOKS = 'webhooks',
	CODES = 'codes',
}

export interface ResourceDataType {
	_id: string;
}

/**
 * Base data structure for table queries
 */
export interface TableData<TResource> {
	docs: TResource[];
	count: number;
}

/**
 * Filter structure for table queries
 */
export interface TableQueryFilters {
	search?: string;
	sort: ResourceSortState;
	customFieldFilters?: Record<string, unknown>;
	list?: string;
	[key: string]: unknown;
}

/**
 * Response structure for table queries
 */
export type TableResponse<TResource, IncludeTotalCount extends boolean = false> = IncludeTotalCount extends true
	? { count: number; docs: TResource[]; totalCount: number }
	: { count: number; docs: TResource[] };

/**
 * Python response structure for table queries
 */
export type TablePythonResponse<TResource, IncludeTotalCount extends boolean = false> = IncludeTotalCount extends true
	? { count: 0; docs: TResource[]; totalCount: number }
	: { count: 0; docs: TResource[] };

/**
 * Field type for table queries
 */
export type FieldType = string | `${string}` | `fields.${string}` | `customer.fields.${string}` | `customer.${string}`;

/**
 * Query variables structure for table queries
 */
export interface TableQueryVariables<Field extends FieldType, Filters extends TableQueryFilters = TableQueryFilters> {
	limit: number;
	skip: number;
	filters: Filters;
	fields: readonly Field[];
	includeTotalCount?: boolean;
	/** When true, `_fields` is only the caller `fields` (falls back to defaults if empty). Default: merge with defaults. */
	replaceFields?: boolean;
	/** Column id → custom field kind. Customer-kind paths expand to named stub sub-paths. */
	customFieldKinds?: Readonly<Record<string, string>>;
}

/**
 * Configuration for resource query hooks
 */
export interface ResourceQueryConfig<TResponse = unknown, TPythonResponse = unknown> {
	/** The resource type */
	resourceType: ResourceType | DocumentType;
	/** Default fields to request if not specified in variables */
	defaultFields?: string[];
	/** Query key prefix (default: resource type) */
	queryKeyPrefix?: string;
	/** Request timeout in milliseconds (default: 10000) */
	timeout?: number;
	/** Stale time in milliseconds (default: 60000). How long data is considered fresh before refetching */
	staleTime?: number;
}

/**
 * Standard Python search response format
 */
export interface PythonSearchResponse<T> {
	count: number;
	docs: T[];
}

/**
 * Standard Python variables for search requests
 */
export interface PythonSearchVariables {
	listName?: string;
	listNames?: string[];
	query?: string;
	_fields?: string;
	_limit?: number;
	_skip?: number;
	_sort?: string;
	[key: string]: unknown;
}

/** Response types */
export interface IdResponse {
	_id: string;
}

export interface IdsResponse {
	_id: string[];
}
